import {
    Box,
    Checkbox,
    CheckboxProps,
    FormControl,
    FormControlLabel,
    formControlLabelClasses,
    FormControlLabelProps,
    FormGroup,
    FormHelperText,
    FormLabel,
    styled,
} from '@mui/material';
import { common } from '@mui/material/colors';
import { colors } from 'common/constants';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
interface ITargetOptionProps
    extends Omit<FormControlLabelProps, 'control' | 'label'> {
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- options can be any
    options: {
        label: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- options can be any
        value: any;
        description?: string;
        image?: string;
    }[];
    row?: boolean;
    label?: string;
    spacing?: number;
    helperText?: React.ReactNode;
    itemsPerRow?: number;
    showOnlyImage?: boolean;
    sizeModifier?: number;
    disabled?: boolean;
}

const BASE_SIZE = 8;

const BpIcon = styled('span')(({ theme }) => ({
    display: 'none',
    borderRadius: 3,
    width: 16,
    height: 16,
    boxShadow:
        'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
    backgroundColor: '#f5f8fa',
    backgroundImage:
        'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
    '.Mui-focusVisible &': {
        outline: '2px auto rgba(19,124,189,.6)',
        outlineOffset: 2,
    },
    'input:hover ~ &': {
        backgroundColor: '#ebf1f5',
        ...theme.applyStyles('dark', {
            backgroundColor: '#30404d',
        }),
    },
    'input:disabled ~ &': {
        boxShadow: 'none',
        background: 'rgba(206,217,224,.5)',
        ...theme.applyStyles('dark', {
            background: 'rgba(57,75,89,.5)',
        }),
    },
    ...theme.applyStyles('dark', {
        boxShadow: '0 0 0 1px rgb(16 22 26 / 40%)',
        backgroundColor: '#394b59',
        backgroundImage:
            'linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))',
    }),
}));

const BpCheckedIcon = styled(BpIcon)({
    backgroundColor: '#137cbd',
    backgroundImage:
        'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
    '&::before': {
        display: 'block',
        width: 16,
        height: 16,
        backgroundImage:
            "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
            " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
            "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
        content: '""',
    },
    'input:hover ~ &': {
        backgroundColor: '#106ba3',
    },
});

function BpCheckbox(props: CheckboxProps) {
    return (
        <Checkbox
            sx={{ '&:hover': { bgcolor: 'transparent' } }}
            disableRipple
            color="default"
            checkedIcon={<BpCheckedIcon />}
            icon={<BpIcon />}
            inputProps={{ 'aria-label': 'Checkbox' }}
            {...props}
        />
    );
}

export function TargetOption({
    row,
    name,
    label,
    options,
    spacing,
    helperText,
    sx,
    itemsPerRow = 1,
    showOnlyImage = false,
    sizeModifier = 1,
    disabled = false,
    ...other
}: ITargetOptionProps) {
    const { control } = useFormContext();
    const two_times_base_size = BASE_SIZE * 2 * sizeModifier;
    const two_and_half_times_base_size = BASE_SIZE * 2.5 * sizeModifier;
    const four_times_base_size = BASE_SIZE * 4 * sizeModifier;
    const eight_and_half_times_base_size = BASE_SIZE * 8.5 * sizeModifier;
    const thirty_one_times_base_size = BASE_SIZE * 31 * sizeModifier;
    const thirty_six_times_base_size = BASE_SIZE * 36 * sizeModifier;

    const getSelected = (selectedItems: string[] = [], item: string) => {
        if (disabled) {
            return selectedItems;
        }
        return selectedItems.includes(item)
            ? selectedItems.filter((value) => value !== item)
            : [...selectedItems, item];
    };
    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                    {label && (
                        <FormLabel
                            component="legend"
                            sx={{ typography: 'body2' }}
                        >
                            {label}
                        </FormLabel>
                    )}
                    {!showOnlyImage && (
                        <FormGroup
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: `repeat(${itemsPerRow}, 1fr)`,
                                gap: spacing || 2,
                                ...(row && {
                                    flexDirection: 'row',
                                }),
                                [`& .${formControlLabelClasses.root}`]: {
                                    ...(row && {
                                        mr: 0,
                                        '&:not(:last-of-type)': {
                                            mr: spacing || 2,
                                        },
                                    }),
                                },
                                ...sx,
                            }}
                        >
                            {options.map((option) => (
                                <FormControlLabel
                                    key={option.value}
                                    control={
                                        <BpCheckbox
                                            checked={field.value?.includes(
                                                option.value
                                            )}
                                            onChange={() =>
                                                field.onChange(
                                                    getSelected(
                                                        field.value,
                                                        option.value
                                                    )
                                                )
                                            }
                                        />
                                    }
                                    label={
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                alignItems: 'center',
                                                fontSize: two_times_base_size,
                                            }}
                                        >
                                            {option.description && (
                                                <Box
                                                    sx={{
                                                        backgroundColor:
                                                            '#FF7916',
                                                        padding: '4px 8px',

                                                        color: common.white,
                                                        fontWeight: '600',
                                                        borderRadius: '30px',
                                                    }}
                                                >
                                                    {option.description}
                                                </Box>
                                            )}
                                            {option.label}
                                        </Box>
                                    }
                                    sx={{
                                        width: '100%',
                                        height: `${eight_and_half_times_base_size}px`,
                                        backgroundColor: field.value?.includes(
                                            option.value
                                        )
                                            ? `${colors.main}`
                                            : common.white,
                                        color: field.value?.includes(
                                            option.value
                                        )
                                            ? common.white
                                            : common.black,
                                        padding: '4px 8px',
                                        margin: 0,
                                        borderRadius: '60px',
                                        marginBottom: `${two_times_base_size}px`,
                                        fontSize: two_and_half_times_base_size,
                                        fontWeight: '400',
                                        lineHeight: '24px',
                                        border: '2px solid #eff1ff',
                                    }}
                                    {...other}
                                />
                            ))}
                        </FormGroup>
                    )}
                    {showOnlyImage && (
                        <FormGroup
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'start',
                                gap: `${four_times_base_size}px`,
                                ...(row && {
                                    flexDirection: 'row',
                                }),
                                ...sx,
                            }}
                        >
                            {options.map((option) => (
                                <FormControlLabel
                                    key={option.value}
                                    control={
                                        <BpCheckbox
                                            checked={field.value?.includes(
                                                option.value
                                            )}
                                            onChange={() =>
                                                field.onChange(
                                                    getSelected(
                                                        field.value,
                                                        option.value
                                                    )
                                                )
                                            }
                                        />
                                    }
                                    label={
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                width: '100%',
                                                height: '100%',
                                                overflow: 'visible',
                                                backgroundColor: common.white,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: '30px',
                                                border: '2px solid #eff1ff',
                                            }}
                                        >
                                            {field.value?.includes(
                                                option.value
                                            ) && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        transform:
                                                            'translate(-50%, -50%)',
                                                        width: `${thirty_six_times_base_size}px`,
                                                        height: `${thirty_six_times_base_size}px`,
                                                        backgroundColor:
                                                            'var(--color-primary-light-20)',
                                                        borderRadius: '30px',
                                                        border: '1px solid var(--color-primary)',
                                                        zIndex: 1,
                                                    }}
                                                ></Box>
                                            )}
                                            <img
                                                src={option.image}
                                                alt="option"
                                                style={{
                                                    width: `${thirty_one_times_base_size}px`,
                                                    height: 'auto',
                                                    objectFit: 'contain',
                                                    zIndex: 2,
                                                }}
                                            />
                                        </Box>
                                    }
                                    sx={{
                                        height: 'auto',
                                        color: field.value?.includes(
                                            option.value
                                        )
                                            ? common.white
                                            : common.black,
                                        margin: 0,
                                        borderRadius: '30px',
                                        marginBottom: `${two_times_base_size}px`,
                                        overflow: 'visible',
                                        position: 'relative',
                                    }}
                                    {...other}
                                />
                            ))}
                        </FormGroup>
                    )}

                    {(!!error || helperText) && (
                        <FormHelperText
                            error={!!error}
                            sx={{
                                fontSize: two_times_base_size,
                                marginLeft: 0,
                                color: `${colors.textError} !important`,
                                fontWeight: '400',
                            }}
                        >
                            {error ? error?.message : helperText}
                        </FormHelperText>
                    )}
                </FormControl>
            )}
        />
    );
}
